/**
 * UniverseDashboard — iter-65.
 *
 * Reusable per-universe mini-dashboard. Each Front-Office universe now has
 * its own dedicated staff page composed of :
 *   • KPI ribbon  (today / week / month / revenue / participants / payments split)
 *   • Advanced filters  (period presets + custom range + experience selector)
 *   • Filtered reservation list scoped to *this universe only*
 *   • CSV / XLSX export buttons that respect the active filters
 *
 * The component is parameterised via a static `UNIVERSE_CONFIGS` map declared
 * below. Adding a new universe = adding a new entry there; no boilerplate.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon, Users, Wallet, TrendingUp, Percent, Filter,
  FileText, Download, RefreshCw, ChevronLeft, Search, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, getStaffToken } from "../../lib/api";

// ── Universe configuration map ────────────────────────────────────
// Each universe : which offer_types belong to it, its human-friendly label,
// its sub-experiences (used both to build the filter dropdown and to power
// per-experience KPIs), and the icon shown in the nav.
const UNIVERSE_CONFIGS = {
  hebergement: {
    label: "Hébergement",
    universe: "Hôtel",
    offer_types: ["hebergement"],
    experiences: [
      { id: "hebergement", label: "Toutes chambres & suites" },
    ],
  },
  salles: {
    label: "Salles & Séminaire",
    universe: "Hôtel",
    offer_types: ["salles", "seminaire", "team_building", "journee_etude", "dejeuner_diner_entreprise", "formule_personnalisee"],
    experiences: [
      { id: "salles", label: "Salles (direct)" },
      { id: "seminaire", label: "Séminaire" },
      { id: "team_building", label: "Team Building" },
      { id: "journee_etude", label: "Journée d'étude" },
      { id: "dejeuner_diner_entreprise", label: "Déjeuner / Dîner entreprise" },
      { id: "formule_personnalisee", label: "Formule personnalisée" },
    ],
  },
  "le-kaai": {
    label: "Restaurant Le Kaai",
    universe: "Hôtel",
    offer_types: ["le_kaai"],
    experiences: [{ id: "le_kaai", label: "Le Kaai (déjeuner / dîner)" }],
  },
  lounge: {
    label: "Lounge",
    universe: "Hôtel",
    offer_types: ["lounge"],
    experiences: [{ id: "lounge", label: "Lounge (cocktails / soir)" }],
  },
  spa: {
    label: "Spa & Hammam",
    universe: "Hôtel",
    offer_types: ["spa_wellness"],
    experiences: [{ id: "spa_wellness", label: "Soins Spa & Hammam" }],
    frozen: true,  // publicly frozen (Coming Soon modal) — staff still sees historical
  },
  "beach-club": {
    label: "Beach Club",
    universe: "Beach Life",
    offer_types: ["pass_day", "sunset", "brunch"],
    experiences: [
      { id: "pass_day", label: "Day Pass" },
      { id: "sunset", label: "Sunset Experience" },
      { id: "brunch", label: "B Brunch" },
    ],
  },
  loazy: {
    label: "Loazy · Loisirs",
    universe: "Beach Life",
    offer_types: ["offres_loisirs"],
    experiences: [{ id: "offres_loisirs", label: "Toutes activités" }],
  },
  events: {
    label: "Événements",
    universe: "Beach Life",
    offer_types: ["special_event"],
    experiences: [{ id: "special_event", label: "Tous événements" }],
  },
};

// ── Period presets (returns [from, to] ISO date strings) ─────────
function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function endOfWeek() {
  const d = new Date(); d.setDate(d.getDate() + (7 - (d.getDay() || 7)));
  return d.toISOString().slice(0, 10);
}
function startOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function endOfMonth() {
  const t = new Date();
  const end = new Date(t.getFullYear(), t.getMonth() + 1, 0);
  return end.toISOString().slice(0, 10);
}

const PERIOD_PRESETS = [
  { key: "today", label: "Aujourd'hui", range: () => [today(), today()] },
  { key: "yesterday", label: "Hier", range: () => [daysAgo(1), daysAgo(1)] },
  { key: "week", label: "Cette semaine", range: () => [daysAgo(new Date().getDay() || 7), endOfWeek()] },
  { key: "month", label: "Ce mois", range: () => [startOfMonth(), endOfMonth()] },
  { key: "last30", label: "30 derniers jours", range: () => [daysAgo(30), today()] },
  { key: "all", label: "Toutes les périodes", range: () => ["", ""] },
];

const fmtXOF = (n) => new Intl.NumberFormat("fr-FR").format(Number(n) || 0) + " XOF";

export default function UniverseDashboard() {
  const { key } = useParams();
  const navigate = useNavigate();
  const config = UNIVERSE_CONFIGS[key];

  useEffect(() => { if (!config) navigate("/staff"); }, [config, navigate]);

  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [experienceFilter, setExperienceFilter] = useState("");
  const [eventId, setEventId] = useState("");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [dateFrom, dateTo] = useMemo(() => {
    if (period === "custom") return [customFrom, customTo];
    const preset = PERIOD_PRESETS.find((p) => p.key === period);
    return preset ? preset.range() : ["", ""];
  }, [period, customFrom, customTo]);

  const queryParams = useCallback(() => {
    if (!config) return {};
    const offerTypesCsv = (experienceFilter || config.offer_types.join(","));
    const p = {
      offer_types: offerTypesCsv,
      limit: 500,
    };
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (eventId) p.event_id = eventId;
    if (submittedSearch.trim()) p.search = submittedSearch.trim();
    return p;
  }, [config, experienceFilter, dateFrom, dateTo, eventId, submittedSearch]);

  const load = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const { data } = await api.get("/staff/bookings", { params: queryParams() });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Impossible de charger les réservations");
    } finally {
      setLoading(false);
    }
  }, [config, queryParams]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => computeKpis(rows), [rows]);

  async function handleExport(fmt) {
    setExporting(true);
    try {
      const params = new URLSearchParams({ ...queryParams(), fmt });
      const token = getStaffToken();
      const r = await fetch(`${API}/staff/bookings/export?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Export ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bbr_${key}_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(`Export impossible (${e.message})`);
    } finally {
      setExporting(false);
    }
  }

  if (!config) return null;

  return (
    <div className="p-4 md:p-8 max-w-[1500px] mx-auto space-y-6" data-testid={`universe-dashboard-${key}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45 mb-2 flex items-center gap-2">
            <Link to="/staff" className="hover:text-[#B8922A] inline-flex items-center gap-1"><ChevronLeft size={11} /> {config.universe}</Link>
          </div>
          <h1 className="font-serif font-light text-3xl md:text-4xl text-[#0A0A0A]">{config.label}</h1>
          {config.frozen && (
            <div className="mt-2 inline-block text-[0.6rem] tracking-[0.3em] uppercase text-amber-700 border border-amber-400 px-2.5 py-1">
              Univers gelé — accès public suspendu
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleExport("csv")} disabled={exporting || loading || rows.length === 0} className="inline-flex items-center gap-2 px-4 py-2 border border-[#0A0A0A]/15 hover:border-[#B8922A] text-[0.6rem] tracking-[0.3em] uppercase disabled:opacity-40" data-testid="export-csv">
            <FileText size={12} /> CSV
          </button>
          <button onClick={() => handleExport("xlsx")} disabled={exporting || loading || rows.length === 0} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] hover:bg-[#B8922A] text-white text-[0.6rem] tracking-[0.3em] uppercase disabled:opacity-40" data-testid="export-xlsx">
            <Download size={12} /> Excel
          </button>
          <button onClick={load} className="p-2 border border-[#0A0A0A]/15 hover:border-[#B8922A]" data-testid="dash-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Filters row */}
      <div className="bg-white border border-[#0A0A0A]/10 p-4 md:p-5 space-y-4" data-testid="dash-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={13} className="text-[#0A0A0A]/45" />
          <span className="text-[0.55rem] tracking-[0.35em] uppercase text-[#0A0A0A]/55 mr-2">Période</span>
          {PERIOD_PRESETS.map((p) => (
            <button key={p.key} onClick={() => setPeriod(p.key)} className={`px-3 py-1.5 text-[0.65rem] tracking-[0.15em] uppercase border ${period === p.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]/15 hover:border-[#B8922A] text-[#0A0A0A]/70"}`} data-testid={`period-${p.key}`}>
              {p.label}
            </button>
          ))}
          <button onClick={() => setPeriod("custom")} className={`px-3 py-1.5 text-[0.65rem] tracking-[0.15em] uppercase border ${period === "custom" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]/15 hover:border-[#B8922A] text-[#0A0A0A]/70"}`}>
            Personnalisée
          </button>
          {period === "custom" && (
            <div className="flex items-center gap-2">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border border-[#0A0A0A]/15 px-2 py-1.5 text-xs" data-testid="date-from" />
              <span className="text-xs text-[#0A0A0A]/45">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border border-[#0A0A0A]/15 px-2 py-1.5 text-xs" data-testid="date-to" />
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-[0.55rem] tracking-[0.3em] uppercase text-[#0A0A0A]/55 mb-1.5">Expérience</span>
            <select value={experienceFilter} onChange={(e) => setExperienceFilter(e.target.value)} className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm" data-testid="filter-experience">
              <option value="">Toutes les expériences ({config.offer_types.length})</option>
              {config.experiences.map((ex) => (
                <option key={ex.id} value={ex.id}>{ex.label}</option>
              ))}
            </select>
          </label>
          {key === "events" && (
            <label className="block">
              <span className="block text-[0.55rem] tracking-[0.3em] uppercase text-[#0A0A0A]/55 mb-1.5">Événement (ID)</span>
              <input type="text" value={eventId} onChange={(e) => setEventId(e.target.value)} placeholder="ex : sunset-beach-2026-04-06" className="w-full border border-[#0A0A0A]/15 px-3 py-2 text-sm" data-testid="filter-event-id" />
            </label>
          )}
          <label className="block md:col-span-1">
            <span className="block text-[0.55rem] tracking-[0.3em] uppercase text-[#0A0A0A]/55 mb-1.5">Recherche client</span>
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/35" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setSubmittedSearch(search); }} onBlur={() => setSubmittedSearch(search)} placeholder="Nom, email, téléphone… (Entrée pour valider)" className="w-full border border-[#0A0A0A]/15 pl-8 pr-3 py-2 text-sm" data-testid="filter-search" />
            </div>
          </label>
        </div>
      </div>

      {/* KPI ribbon */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dash-kpis">
        <Kpi icon={CalendarIcon} label="Réservations" value={kpis.count} sub={loading ? "chargement…" : `sur ${prettyPeriod(dateFrom, dateTo)}`} />
        <Kpi icon={Users} label="Participants" value={kpis.pax} sub={`${kpis.adults} adultes · ${kpis.children} enfants`} />
        <Kpi icon={Wallet} label="CA total" value={fmtXOF(kpis.revenue)} sub={`Encaissé : ${fmtXOF(kpis.paid)}`} accent />
        <Kpi icon={TrendingUp} label="Solde à percevoir" value={fmtXOF(kpis.balance)} sub={`${kpis.pendingSolde} avec acompte`} />
        <Kpi icon={Percent} label="Acompte 30 %" value={`${kpis.pct30}%`} sub={`${kpis.count30} rés.`} />
        <Kpi icon={Percent} label="Paiement 100 %" value={`${kpis.pct100}%`} sub={`${kpis.count100} rés.`} />
      </div>

      {/* Reservations list */}
      <div className="bg-white border border-[#0A0A0A]/10" data-testid="dash-bookings-list">
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-[#0A0A0A]/10">
          <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#0A0A0A]/55">
            Réservations · {config.label}
          </div>
          <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#0A0A0A]/50">
            {rows.length} résultats
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#0A0A0A]/45">Chargement…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#0A0A0A]/50">Aucune réservation ne correspond aux filtres.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF7F2] text-[0.55rem] tracking-[0.25em] uppercase text-[#0A0A0A]/60">
                <tr>
                  <th className="text-left px-4 py-2.5">Réf.</th>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Offre</th>
                  <th className="text-left px-4 py-2.5">Client</th>
                  <th className="text-right px-4 py-2.5">Pax</th>
                  <th className="text-right px-4 py-2.5">Total</th>
                  <th className="text-right px-4 py-2.5">Payé</th>
                  <th className="text-right px-4 py-2.5">Solde</th>
                  <th className="text-center px-4 py-2.5">Statut</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const total = Number(r.total_amount || 0);
                  const paid = Number(r.paid_amount || 0);
                  const balance = Number(r.balance_due ?? Math.max(0, total - paid));
                  const isDeposit = r.payment_method === "deposit";
                  return (
                    <tr key={r.id} className="border-t border-[#0A0A0A]/6 hover:bg-[#FAF7F2]/50">
                      <td className="px-4 py-2 font-mono text-[11px] text-[#0A0A0A]/70">{(r.id || "").slice(0, 8).toUpperCase()}</td>
                      <td className="px-4 py-2">{r.date || "—"}{r.boat_time ? ` · ${r.boat_time}` : ""}</td>
                      <td className="px-4 py-2">{r.offer_name || r.offer_type}</td>
                      <td className="px-4 py-2">{r.name} {r.surname}<div className="text-[11px] text-[#0A0A0A]/45">{r.phone || r.email}</div></td>
                      <td className="px-4 py-2 text-right">{(r.adults || 0) + (r.children || 0)}</td>
                      <td className="px-4 py-2 text-right">{fmtXOF(total)}</td>
                      <td className="px-4 py-2 text-right">{fmtXOF(paid)}{isDeposit && <div className="text-[10px] text-amber-700">{r.deposit_pct}% acompte</div>}</td>
                      <td className={`px-4 py-2 text-right ${balance > 0 ? "text-amber-700 font-medium" : "text-[#0A0A0A]/40"}`}>{balance > 0 ? fmtXOF(balance) : "—"}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-block text-[10px] tracking-widest uppercase px-2 py-1 border ${statusStyle(r.status)}`}>
                          {r.status || "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[10px] text-[#0A0A0A]/40 text-right">
        <Link to="/staff/reservations" className="hover:text-[#B8922A] inline-flex items-center gap-1" data-testid="link-global-history">
          Historique global (admin) <ArrowRight size={10} />
        </Link>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────
function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`bg-white border ${accent ? "border-[#B8922A]/40" : "border-[#0A0A0A]/10"} p-4`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[#0A0A0A]/55">{label}</span>
        <Icon size={13} className={accent ? "text-[#B8922A]" : "text-[#0A0A0A]/40"} />
      </div>
      <div className={`font-serif ${accent ? "text-[#B8922A]" : "text-[#0A0A0A]"} text-xl md:text-2xl leading-tight`}>{value}</div>
      {sub && <div className="text-[10px] text-[#0A0A0A]/50 mt-1">{sub}</div>}
    </div>
  );
}

function computeKpis(rows) {
  const count = rows.length;
  let adults = 0, children = 0, revenue = 0, paid = 0, balance = 0;
  let count30 = 0, count100 = 0, pendingSolde = 0;
  rows.forEach((r) => {
    const a = Number(r.adults || 0);
    const c = Number(r.children || 0);
    const total = Number(r.total_amount || 0);
    const p = Number(r.paid_amount || 0);
    const bal = Number(r.balance_due ?? Math.max(0, total - p));
    adults += a; children += c; revenue += total; paid += p; balance += bal;
    if (r.payment_method === "deposit") { count30 += 1; if (bal > 0) pendingSolde += 1; }
    else if (r.payment_method === "fineo" || r.paid_amount === r.total_amount) { count100 += 1; }
  });
  const pct30 = count ? Math.round((count30 / count) * 100) : 0;
  const pct100 = count ? Math.round((count100 / count) * 100) : 0;
  return {
    count, pax: adults + children, adults, children,
    revenue, paid, balance, pct30, pct100, count30, count100, pendingSolde,
  };
}

function prettyPeriod(from, to) {
  if (!from && !to) return "toutes périodes";
  if (from === to) return from;
  return `${from || "…"} → ${to || "…"}`;
}

function statusStyle(s) {
  switch (s) {
    case "confirmed": return "text-emerald-700 border-emerald-300 bg-emerald-50";
    case "cancelled": return "text-red-700 border-red-300 bg-red-50";
    case "pending": return "text-amber-700 border-amber-300 bg-amber-50";
    default: return "text-[#0A0A0A]/60 border-[#0A0A0A]/15";
  }
}
